/**
 * HostingAccountsManagement.tsx — admin view of provisioned customer
 * hosting accounts and provisioning attempts.
 *
 * Two sub-tabs:
 *   - Accounts       — all hosting_accounts rows, with suspend / unsuspend
 *                      / open-customer-detail-page
 *   - Provisioning   — recent provisioning attempts, with retry on failed
 *                      / awaiting_admin rows
 *
 * Reuses backend endpoints we already shipped:
 *   GET  /api/hosting/admin/accounts
 *   POST /api/hosting/admin/accounts/:id/suspend
 *   POST /api/hosting/admin/accounts/:id/unsuspend
 *   GET  /api/hosting/admin/provisioning
 *   POST /api/hosting/admin/provisioning/:attemptId/retry
 */

import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../../config/environment';

interface HostingAccountRow {
  id: string;
  primary_domain: string | null;
  hostname: string | null;
  ip_address: string | null;
  status: string;
  container_vmid: number | null;
  proxmox_host_id: number | null;
  proxmox_host: string | null;
  package_name: string | null;
  customer_email: string | null;
  created_at: string;
  suspended_at: string | null;
}

interface ProvisioningAttemptRow {
  id: string;
  order_id: string;
  order_number: string | null;
  customer_email: string | null;
  status: string;
  last_step: string | null;
  last_error: string | null;
  primary_domain: string | null;
  container_vmid: number | null;
  hosting_account_id: string | null;
  attempts: number;
  pricing_paid: number | null;
  stripe_payment_intent_id: string | null;
  created_at: string;
  completed_at: string | null;
  updated_at: string;
}

const HostingAccountsManagement: React.FC = () => {
  const [subTab, setSubTab] = useState<'accounts' | 'attempts'>('accounts');
  return (
    <div>
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          <SubTabButton active={subTab === 'accounts'} onClick={() => setSubTab('accounts')}>
            Customer Accounts
          </SubTabButton>
          <SubTabButton active={subTab === 'attempts'} onClick={() => setSubTab('attempts')}>
            Provisioning Attempts
          </SubTabButton>
        </nav>
      </div>
      {subTab === 'accounts' ? <AccountsTable /> : <AttemptsTable />}
    </div>
  );
};

const SubTabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`py-3 px-1 border-b-2 text-sm font-medium ${
      active ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    {children}
  </button>
);

// ============================================================================
// Accounts table
// ============================================================================

const AccountsTable: React.FC = () => {
  const [rows, setRows] = useState<HostingAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'suspended' | 'creating' | 'terminated' | 'failed'>('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [showComp, setShowComp] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl('/hosting/admin/accounts'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) setRows(json.data || []);
      else setErr(json.error || 'Failed to load');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const suspend = async (id: string) => {
    const reason = window.prompt('Reason for suspension? (visible in DB only, the customer sees a generic page)');
    if (reason === null) return;
    setBusy(id);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl(`/hosting/admin/accounts/${id}/suspend`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || 'Suspended by admin' }),
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Suspend failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(null);
    }
  };

  const unsuspend = async (id: string) => {
    if (!window.confirm('Unsuspend this hosting account? Public site will be restored.')) return;
    setBusy(id);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl(`/hosting/admin/accounts/${id}/unsuspend`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Unsuspend failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(null);
    }
  };

  const filtered = rows
    .filter(r => filter === 'all' || r.status === filter)
    .filter(r => {
      if (!search.trim()) return true;
      const s = search.trim().toLowerCase();
      return (
        (r.primary_domain && r.primary_domain.toLowerCase().includes(s)) ||
        (r.customer_email && r.customer_email.toLowerCase().includes(s)) ||
        (r.hostname && r.hostname.toLowerCase().includes(s)) ||
        (r.container_vmid && String(r.container_vmid).includes(s))
      );
    });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          className="border border-gray-300 rounded px-3 py-1.5 text-sm flex-1 min-w-[200px]"
          placeholder="Search domain, email, hostname, vmid…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={filter}
          onChange={e => setFilter(e.target.value as any)}
          className="border border-gray-300 rounded px-2 py-1.5 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="creating">Creating</option>
          <option value="terminated">Terminated</option>
          <option value="failed">Failed</option>
        </select>
        <button onClick={load} className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded">Refresh</button>
        <button onClick={() => setShowComp(true)}
                className="text-sm bg-slate-900 text-white hover:bg-slate-800 px-3 py-1.5 rounded font-medium">
          + Provision comp account
        </button>
        <div className="text-xs text-gray-500 ml-2">{filtered.length} of {rows.length}</div>
      </div>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}
      {notice && <div className="mb-3 bg-green-50 border border-green-200 rounded p-2 text-sm text-green-700">{notice}</div>}

      {showComp && (
        <CompProvisionModal
          onClose={() => setShowComp(false)}
          onDone={(msg) => { setShowComp(false); setNotice(msg); load(); }}
        />
      )}

      {loading ? (
        <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg p-12 text-center text-gray-500">No hosting accounts match.</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-gray-600">
                <th className="px-4 py-2 font-medium">Domain</th>
                <th className="px-4 py-2 font-medium">Customer</th>
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 font-medium">Host / VMID</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-blue-50/30">
                  <td className="px-4 py-2 font-mono text-xs break-words max-w-xs">
                    {r.primary_domain || <span className="text-gray-400">(no domain)</span>}
                    {r.primary_domain && (
                      <a href={`https://${r.primary_domain}`} target="_blank" rel="noreferrer" className="text-blue-500 ml-1">↗</a>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{r.customer_email || '—'}</td>
                  <td className="px-4 py-2 text-xs">{r.package_name || '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.proxmox_host || `host#${r.proxmox_host_id}`} <span className="text-gray-400">/</span> {r.container_vmid ?? '—'}</td>
                  <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-2 text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-right">
                    <a href={`/customer/hosting/${r.id}`} target="_blank" rel="noreferrer" className="text-blue-600 text-xs hover:underline mr-3">Open</a>
                    {r.status === 'active' && (
                      <button onClick={() => suspend(r.id)} disabled={busy === r.id} className="text-amber-700 text-xs hover:underline mr-3">Suspend</button>
                    )}
                    {r.status === 'suspended' && (
                      <button onClick={() => unsuspend(r.id)} disabled={busy === r.id} className="text-green-700 text-xs hover:underline">Unsuspend</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Comp provisioning modal — internal/migrated accounts, no payment.
// ============================================================================

interface PackageOption { id: string; name: string; type?: string; price_monthly?: number }

const CompProvisionModal: React.FC<{ onClose: () => void; onDone: (msg: string) => void }> = ({ onClose, onDone }) => {
  const [packages, setPackages] = useState<PackageOption[]>([]);
  const [userEmail, setUserEmail] = useState('');
  const [packageId, setPackageId] = useState('');
  const [domain, setDomain] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(getApiUrl('/hosting/packages'));
        const json = await resp.json();
        const list: PackageOption[] = (json.data || json.packages || [])
          .filter((p: PackageOption) => !p.type || (p.type !== 'vps' && p.type !== 'email'));
        setPackages(list);
        if (list.length > 0) setPackageId(list[0].id);
      } catch { /* package list is a nicety; the form still validates server-side */ }
    })();
  }, []);

  const submit = async () => {
    setErr('');
    if (!userEmail.trim() || !packageId || !domain.trim()) {
      setErr('Customer email, package and domain are all required.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl('/hosting/admin/comp-provision'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: userEmail.trim(), packageId, domain: domain.trim(), note: note.trim() || undefined }),
      });
      const json = await resp.json();
      if (!json.success) { setErr(json.error || 'Provisioning failed to start'); return; }
      onDone(`${json.message || 'Provisioning started.'} Order ${json.data?.orderNumber || ''} — track progress in the Provisioning Attempts tab.`);
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-slate-900/40" />
      <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Provision comp account</h3>
        <p className="text-xs text-gray-500 mb-4">
          Creates a $0 internal order (INT-…) and runs the normal provisioning pipeline.
          No Stripe, no invoice, excluded from revenue. For internal &amp; migrated sites.
        </p>

        {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

        <label className="block text-xs font-medium text-gray-600 mb-1">Customer email (must exist)</label>
        <input value={userEmail} onChange={e => setUserEmail(e.target.value)} type="email"
               placeholder="customer@example.com" autoFocus
               className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" />

        <label className="block text-xs font-medium text-gray-600 mb-1">Hosting package</label>
        <select value={packageId} onChange={e => setPackageId(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3 bg-white">
          {packages.length === 0 && <option value="">Loading packages…</option>}
          {packages.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}{p.price_monthly != null ? ` — $${p.price_monthly}/mo (comped)` : ''}
            </option>
          ))}
        </select>

        <label className="block text-xs font-medium text-gray-600 mb-1">Primary domain (existing — no registration happens)</label>
        <input value={domain} onChange={e => setDomain(e.target.value)}
               placeholder="example.com"
               className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3 font-mono" />

        <label className="block text-xs font-medium text-gray-600 mb-1">Note (why is this comped?)</label>
        <input value={note} onChange={e => setNote(e.target.value)}
               placeholder="e.g. migrated from old server"
               className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-5" />

        <div className="flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded text-sm text-gray-600 hover:text-gray-900">Cancel</button>
          <button onClick={submit} disabled={submitting}
                  className="px-4 py-2 rounded bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60">
            {submitting ? 'Starting…' : 'Provision (no charge)'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Provisioning attempts table
// ============================================================================

const AttemptsTable: React.FC = () => {
  const [rows, setRows] = useState<ProvisioningAttemptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState<'all' | 'in_progress' | 'completed' | 'failed' | 'awaiting_admin' | 'refunded' | 'pending'>('all');
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl('/hosting/admin/provisioning'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) setRows(json.data || []);
      else setErr(json.error || 'Failed to load');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const retry = async (id: string) => {
    if (!window.confirm('Re-run provisioning for this attempt? It will pick up where it left off.')) return;
    setBusy(id);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(getApiUrl(`/hosting/admin/provisioning/${id}/retry`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) await load();
      else setErr(json.error || 'Retry failed');
    } catch (e: any) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(null);
    }
  };

  const filtered = rows.filter(r => filter === 'all' || r.status === filter);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <select
          value={filter}
          onChange={e => setFilter(e.target.value as any)}
          className="border border-gray-300 rounded px-2 py-1.5 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="awaiting_admin">Awaiting admin</option>
          <option value="refunded">Refunded</option>
          <option value="pending">Pending</option>
        </select>
        <button onClick={load} className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded">Refresh</button>
        <div className="text-xs text-gray-500 ml-2">{filtered.length} of {rows.length}</div>
      </div>

      {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

      {loading ? (
        <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg p-12 text-center text-gray-500">No provisioning attempts match.</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-gray-600">
                <th className="px-4 py-2 font-medium">Order</th>
                <th className="px-4 py-2 font-medium">Domain</th>
                <th className="px-4 py-2 font-medium">Customer</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Last step</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-blue-50/30">
                  <td className="px-4 py-2 font-mono text-xs">{r.order_number || r.order_id.slice(0, 8)}</td>
                  <td className="px-4 py-2 font-mono text-xs break-words max-w-xs">{r.primary_domain || '—'}</td>
                  <td className="px-4 py-2 text-xs">{r.customer_email || '—'}</td>
                  <td className="px-4 py-2">
                    <AttemptStatusPill status={r.status} />
                    {r.last_error && (
                      <div className="text-xs text-red-700 mt-1 font-mono break-words max-w-xs">{r.last_error.slice(0, 200)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{r.last_step || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {new Date(r.created_at).toLocaleString()}
                    <div className="text-gray-400">attempts: {r.attempts}</div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    {(r.status === 'failed' || r.status === 'awaiting_admin') && (
                      <button onClick={() => retry(r.id)} disabled={busy === r.id} className="text-blue-600 text-xs hover:underline mr-3">Retry</button>
                    )}
                    {r.hosting_account_id && (
                      <a href={`/customer/hosting/${r.hosting_account_id}`} target="_blank" rel="noreferrer" className="text-blue-600 text-xs hover:underline">Open account</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Status pills
// ============================================================================

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    creating: 'bg-blue-100 text-blue-800',
    suspended: 'bg-amber-100 text-amber-800',
    terminated: 'bg-gray-200 text-gray-700',
    failed: 'bg-red-100 text-red-800',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

const AttemptStatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    awaiting_admin: 'bg-amber-100 text-amber-800',
    refunded: 'bg-purple-100 text-purple-800',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

export default HostingAccountsManagement;
